import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// util
import { cloneDeep, find, findIndex, forEach, get, isArray } from 'lodash';

import { Store } from '@ngrx/store';
import { Observable, BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';

// models
import { AppState, UPDATE_CONTENT } from '../app-reducer';
import { ModalComponent } from 'src/app/components/modal.component';

import { ProcessorService } from './processor.service';
import { ApiService } from 'src/app/services/api.service';
import { QuestionService } from '../services/question.service';
import { visitNode } from 'typescript';

@Injectable({
  providedIn: 'root'
})

export class NavigationService {
  appState$: Observable<any>;
  state: any;
  menu: any = {};
  questions: any;
  multipleResponseButtonDisabled: BehaviorSubject<boolean>;
  routes: any;
  sendEmailModalPopped: boolean = false;
  buttonDisabled: BehaviorSubject<boolean>;
  hideMenu: BehaviorSubject<boolean>;
  submitOnExit: BehaviorSubject<boolean>;
  submitReady: BehaviorSubject<boolean>;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    public processorService: ProcessorService,
    private questionService: QuestionService,
    private apiService: ApiService

  ) {
    this.appState$ = store.select('sections');
    this.multipleResponseButtonDisabled = new BehaviorSubject<boolean>(false);
    this.buttonDisabled = new BehaviorSubject<boolean>(false);
    this.hideMenu = new BehaviorSubject<boolean>(false);
    this.submitOnExit = new BehaviorSubject<boolean>(false);
    this.submitReady = new BehaviorSubject<boolean>(false);
  }

  buildNavStore(routes: any) {

    let menuData = [];
    let stateContent = {};
    let stateQuestions = [];

    this.appState$.pipe(first()).subscribe((state: any) => {
      this.state = state;

      // questions
      this.questions = cloneDeep(state.questions);

      menuData = state.content.menuData ? state.content.menuData : null
      stateContent = state.content ? state.content : null;
      stateQuestions = state.questions ? state.questions : null;

      this.setOwnerSignatories(state);


    });

    if (menuData) {
       this.addPolicyOwnerBlock(menuData);
       this.removeTrustCertBlock(menuData);
       this.setVisibilityByPermissions(menuData);
       this.resetDisclosuresForOwner();
       return;
     }

    const payload = {'menuData': []};
    const allBlocks: any = cloneDeep(this.router.config.filter(item => item.data && item.data.menuLabel));

    allBlocks.forEach((block, index) => {
      payload['menuData'][index] = {
        'label': block.data.menuLabel,
        'name': block.data.blockName,
        'path': block.path,
        'valid': null,
        'visible': true,
        'questions': []
      };
      if (block.data.blockConditionalClause) {
        const questionBlock = this.questions.find(element => element.Name === payload['menuData'][index].name);
        const conditionalQuestion = questionBlock.Questions.find(element => element.QuestionName === block.data.blockConditionalClause);
        payload['menuData'][index].conditionalClause = conditionalQuestion.ConditionalClause;
        payload['menuData'][index].visible = false;
      }
      if (index > 34) {
        payload['menuData'][index].visible = false;
      }
      delete payload['menuData'][index].canActivate;
      delete payload['menuData'][index].component;
      delete payload['menuData'][index].data;
    });


    // build menuNav if resuming an existing case that doesn't have it
    if (stateContent !== undefined) {
      this.buildNewMenuData(stateContent, stateQuestions, payload);
      this.resetDisclosuresForOwner();
    }
    // update store
    this.store.dispatch({
      type: UPDATE_CONTENT,
      payload: window['JSON'].parse(window['JSON'].stringify(payload))
    });
  }

  removeTrustCertBlock(menuData: any[]) {
    if (menuData.filter(x=>x.name == 'TrustCertification').length > 0){
      console.log('removing trust cert from menudata');
      const trustCertBlockIndex = this.state.content.menuData.findIndex(x => x.name === 'TrustCertification');
      menuData.splice(trustCertBlockIndex, 1);
    }
  }

  setVisibilityByPermissions(menuData) {
    menuData.forEach((block, index)=>{
      //console.log('block: ', block);
      const section = find(this.state.questions, { Name: block.name });
      const isViewable = this.questionService.checkIfViewable(get(section, "QuestionBlockPermissions"), this.state.userRole, this.state.isDemo);
      //console.log('isViewable: ', isViewable);
          if (!isViewable){
            block.visible = false;
          } else if (isViewable && block.valid){ //allow for blocks to be seen after being hidden for previous role
            block.visible = true;
          }
          if (block.name == 'SECTIONBPolicyOwner'){
              block.visible = true;
          }
    })
  }

  buildNewMenuData(stateContent: {}, stateQuestions, payload) {
    //console.log('making new menudata');
    const notBlocks = ['agentData', 'agentInfo', 'payLoadHash', 'validBlocks'];

    forEach(stateContent, (val, block) => {
      if (!notBlocks.includes(block)) {
        const blockQuestions = stateQuestions.find(x => x.Name === block)
        if (blockQuestions){
          const entry = payload['menuData'].find(x => x.name === block);
          forEach(blockQuestions.Questions, q => {
            const isRequired = q.validators && q.validators.includes('required') && q.controlType !== 'textarea';
            if (val[q.QuestionName] && val[q.QuestionName] !== null) {
              entry.questions.push({name:q.QuestionName, visible: true, valid: true});
            } else if (isRequired) {
              entry.questions.push({name:q.QuestionName, visible: true, valid: null});
            } else {
              entry.questions.push({name:q.QuestionName, visible: null, valid: null});
            }
          });
          entry.visible = true;
          // Check if all questions in the block are valid
          entry.valid = entry.questions.every(question => question.valid === true);
       //   entry.valid = true;
          if (entry.conditionalClause == '0') {
              entry.visible = false;
          }
          const section = find(this.state.questions, { Name: block });
          const isViewable = this.questionService.checkIfViewable(get(section, "QuestionBlockPermissions"), this.state.userRole, this.state.isDemo);
          if (!isViewable){
            entry.visible = false;
          }
        }
      }
    });
  }

  //forces owners to look at disclosures
  resetDisclosuresForOwner() {

    if (this.state.userRole.name.includes('Owner') && !this.state.userRole.name.includes('Insured')){
      this.setBlockValidity('Disclosures', null);
    }
  }

  addPolicyOwnerBlock(menuData) {
    if (menuData.filter(x=>x.name == 'SECTIONBPolicyOwner').length == 0){
      console.log('adding menu data for policy owner block');
      const policyOwnerBlockInfo: any = cloneDeep(this.router.config.filter(item => item.data && item.data.blockName =='SECTIONBPolicyOwner')[0]);
      const policyOwnerBlockMenuObject = {
        'label': policyOwnerBlockInfo.data.menuLabel,
        'name': policyOwnerBlockInfo.data.blockName,
        'path': policyOwnerBlockInfo.path,
        'valid': null,
        'visible': true,
        'questions': []
      };
      menuData.splice(1, 0, policyOwnerBlockMenuObject);
    }
  }
  setOwnerSignatories(state: any) {
    if (this.checkPolicyOwnerInfoExists(state)){
      const payload = {
        'AppId': get(state, 'appId'),
        'AppAuthId': state.appAuthId
      };
      this.apiService.getOwnerSignatories(payload).subscribe(response => {
        if (response.code === 200 && response.data) {
          this.store.dispatch({
            type: UPDATE_CONTENT,
            payload: { signators: response.data.OwnershipSignatories }
          })
        }
      });
    }
  }

  navStoreExists() {
    this.appState$.pipe(first()).subscribe((state: any) => {

      // questions
      this.questions = cloneDeep(state.questions);

    });
  }

  async updateNav(formControls: any, blockName: any, visibleQuestions?, allValid=true) {

    const appBlockStatus = [];

    this.appState$.pipe(first()).subscribe((state: any) => {

      const currentIndex = state.content.menuData.findIndex(x => x.name === blockName);
      const isInsured = state.userRole.name.includes('Insured');

      for (let i = currentIndex; i <  state.content.menuData.length; i++) {
        const thisBlock = state.content.menuData[i];
        //console.log('obj: ', formControls);
        if (thisBlock.questions.some(obj => obj.visible && obj.valid === false && formControls[obj]?.enabled)) {
          thisBlock.valid = false;
          console.log('why did', thisBlock.name, 'just turn red?');
          console.log(cloneDeep(thisBlock.questions.filter(x => x.visible && x.valid === false)));
        }
      };
      state.content.menuData.forEach((block, index) => {
        //console.log('block: ', block.name, ' valid: ', block.valid, " show: ", cloneDeep(block.visible));
        appBlockStatus.push(block.valid || !block.visible ? true : false);
      });

      const customerSigned = state.userRole.customerSigned === 0 ? false : true;
      const disclosuresBlock = state.content.menuData.findIndex(x => x.name == 'Disclosures');
      const disclosuresReady = isInsured && !appBlockStatus.slice(0, disclosuresBlock).includes(false) && allValid || !isInsured; //previously readyToSignPI
      const agentFinalPass = customerSigned && state.userRole.name == 'Agent';
      disclosuresReady ? this.processorService.notifyFormChange('VALID') : this.processorService.notifyFormChange('INVALID');
      console.log('disclosuresReady', disclosuresReady);
      console.log('allValid', allValid);
      console.log('blockName', blockName);
     // console.log('isOwner', isOwner);

      const disclosuresValid = state.content.menuData[disclosuresBlock].valid;
      state.content.menuData[disclosuresBlock].visible = disclosuresReady;
      this.setInsuredDeclarationsVisibility(state, customerSigned, !isInsured ? disclosuresValid : disclosuresReady);
      this.setAgentFinalPassVisibility(state, agentFinalPass);
      //only show owner declarations to owner
      this.setOwnerDeclarationsVisibility(state, agentFinalPass, customerSigned,  !isInsured ? disclosuresValid : disclosuresReady);
      this.setAgentDeclarationsVisibility(state, customerSigned);
    });
  }

  setInsuredDeclarationsVisibility(state: any, customerSigned, disclosuresValid) {
    const declarationsInsuredBlock = state.content.menuData.findIndex(x => x.name == 'DeclarationsInsured');
    const readyToSignPI = state.content.menuData ? (cloneDeep(state.content.menuData.filter(x => x.visible && x.valid != true && x.name!='DeclarationsInsured')).length >= 1 ? false : true) : false;
      //console.log('readyToSignPI: ', readyToSignPI);
    state.content.menuData[declarationsInsuredBlock].visible = (readyToSignPI && state.userRole.name !== 'Agent') && disclosuresValid || customerSigned && disclosuresValid;
  }

  setAgentFinalPassVisibility(state: any, agentFinalPass: boolean) {
    const agentReportBlock = state.content.menuData.findIndex(x => x.name == 'AgentReport');
    const transmittalBlock = state.content.menuData.findIndex(x => x.name == 'TransmittalBlock');

    state.content.menuData[agentReportBlock].visible = agentFinalPass && state.content.menuData ? (cloneDeep(state.content.menuData.filter(x => x.visible && x.valid != true && x.name!='AgentReport' && x.name!='TransmittalBlock' && x.name!='DeclarationsAgent')).length >= 1 ? false : true) : false;
    state.content.menuData[transmittalBlock].visible = agentFinalPass && state.content.menuData[agentReportBlock].visible;
  }

  setAgentDeclarationsVisibility(state: any, customerSigned: boolean) {
    const declarationsAgentBlock = state.content.menuData.findIndex(x => x.name == 'DeclarationsAgent');

    const readyToSignAgent = state.content.menuData ? (cloneDeep(state.content.menuData.filter(x => x.visible && x.valid != true && x.name!='DeclarationsAgent')).length >= 1 ? false : true) : false;
      //console.log('readyToSignAgent: ', readyToSignAgent);
    state.content.menuData[declarationsAgentBlock].visible = (customerSigned && readyToSignAgent && state.userRole.name == 'Agent') ? true : false;
  }

  setOwnerDeclarationsVisibility(state, agentFinalPass, customerSigned, disclosuresValid) {
    const ownerList = ['OwnerA', 'OwnerB', 'OwnerC', 'OwnerD', 'OwnerE', 'OwnerF', 'OwnerG', 'OwnerH', 'OwnerI', 'OwnerJ']
    const ownerDeclarationsReady = customerSigned && disclosuresValid;
    if (this.checkPolicyOwnerInfoExists(state)){
      ownerList.forEach((owner, index)=>{
        const ownerMenuIndex = state.content.menuData.findIndex(x => x.name == 'Declarations'.concat(owner));
        if (ownerMenuIndex && state.content.menuData[ownerMenuIndex]) {
          state.content.menuData[ownerMenuIndex].visible = (ownerDeclarationsReady && state.userRole.name === owner) ? true : false;
          if (agentFinalPass){
            this.setAgentViewOwnerDeclarations(state, ownerMenuIndex, owner);
          }
        }
      })
    }
  }

  setAgentViewOwnerDeclarations(state: any, ownerMenuIndex: any, owner: string) {
    if (state.content.menuData[ownerMenuIndex].valid){
      state.content.menuData[ownerMenuIndex].visible = true;
    } else if(state.content.signators ? state.content.signators.find(x=> x.SignerLetter == owner.replace('Owner', '')) : false){
      state.content.menuData[ownerMenuIndex].visible = true;
    }
  }
  checkPolicyOwnerInfoExists(state: any) {
    return (state["content"]["SECTIONBPolicyOwner"] ? state["content"]["SECTIONBPolicyOwner"]["PolicyOwnerInformation"] : false);
  }

  // move to the next section
  nextSection(section: string) {
    this.router.navigate([section], { queryParams: { auth: true } });
  }

  // move to the next section relative to base
  nextSectionRelative(section: string) {
    this.router.navigate(['express', section], { queryParams: { auth: true } });
  }

  nextSectionRelativeError(section: string) {
    this.router.navigate(['express', section], );
  }

    // move to the next section relative to base
  nextSectionRelativeNewWindow(section: string) {
  // console.log('creating URL');
    const url = this.router.serializeUrl(this.router.createUrlTree(['express', section], { queryParams: { auth: true } }));
  // console.log (url);
    window.open(url, '_blank');
  }

      // move to the next section relative to base for demo
  nextSectionRelativeNewWindowDemo(section: string) {
    // console.log('creating URL');
      const url = this.router.serializeUrl(this.router.createUrlTree(['express', section], { queryParams: { auth: true } }));
    // console.log (url);
      window.open(url, '_blank');
      window.setTimeout(function(){this.close();},500);
    }

  takeMeTo(formValue: any, isEditable: boolean, routeConfig: any, path: string, changedStatus = false) {

    // alias for section data
    const { data: sectionData, path: currentUrlPath } = routeConfig;

    this.appState$.pipe(first()).subscribe((state: any) => {

      this.state = state;

      // merge values
      const payload = {};
      payload[sectionData.blockName] = { ...formValue };

      if (isEditable) {
        // update store
        this.store.dispatch({
          type: UPDATE_CONTENT,
          payload: window['JSON'].parse(window['JSON'].stringify(payload))
        });
      }

      // sets form value variable used in transformPayload method
      this.processorService.eraseShownQuestions(formValue);

      const sectionQuestions = this.questions.find(item => item.Name === sectionData.blockName);

      // prepare payload (uses formValue clone because the recursive method mutates the passed-in object)
      let updatedPayload = this.processorService.transformPayload(cloneDeep(formValue), sectionQuestions.Questions);

      // removes null values from Disclosures, which needs only "DisclosuresSubmitted," to prevent validation errors in JHADE
      if (sectionData.blockName === 'Disclosures') {
        updatedPayload = Object.entries(updatedPayload).reduce((a,[k,v]) => (v ? (a[k]=v, a) : a), {});
        if (state.userRole.name.includes('Insured')) {
          changedStatus = true;
        }
      }

      // Handles CA-specific medical conditions that need to be mapped to ConditionDiagnosisOtherDetails
      if (sectionData.blockName === 'Part2SECTIONCMedicalConditions2' && state.content.SECTIONAProposedInsured?.StateOfIssue === 'CA') {
        updatedPayload = this.processorService.handleCaliforniaMedical(updatedPayload, sectionQuestions.Questions);
      }

      if (sectionData.blockName === 'SECTIONBPolicyOwner') {
        updatedPayload = this.processorService.reFormatTINResponse(updatedPayload);
      }

      const { appAuthId } = state;

      if (isEditable) {
        // check if start-app and process-application calls have completed
        if (this.submitReady.value) {
          // Submit answers to API
          this.processorService.submitSection({ appAuthId: appAuthId, section: sectionData.blockName, payload: updatedPayload }, changedStatus);
        } else {
          // wait for start-app and process-application calls to complete, then submit block
          const waitForSignal = this.isReadyToSubmit().subscribe(go => {
            if (go) {
              this.processorService.submitSection({ appAuthId: appAuthId, section: sectionData.blockName, payload: updatedPayload }, changedStatus);
              waitForSignal.unsubscribe();
            }
          });
        }
      }
    });

    if (path === 'fwd' || path === 'back') {
      path = this.skipConditionalBlock(currentUrlPath, this.questions, formValue, path);
    } else if (path === 'exit') {
      return;
    }

    this.nextSection(path);
  }

  skipConditionalBlock(currentUrlPath, questions, formValue, direction): string {

    // skip conditionalClause analysis when hitting next on last page in app definition
    if (direction === 'fwd' && currentUrlPath === 'express/declarations-agent') {
      return 'express/processing';
    }

    // get current route
    const routes = this.router.config;
    const currentRouteIndex = findIndex(routes, { path: currentUrlPath });

    let nextPage: any;
    let i = 1;
    while (i < 15) {
      if (direction == 'fwd') {
        nextPage = routes[currentRouteIndex + i];
      } else if (direction == 'back') {
        nextPage = routes[currentRouteIndex - i];
      }

      // check if next section is conditional block
      const hasBlockConditionalClause = get(nextPage, 'data.blockConditionalClause') ? true : false;
      const section = find(this.state.questions, { Name: nextPage.data.blockName });
      const condition = find(section.Questions, { QuestionName: nextPage.data.blockConditionalClause });

    //  console.log('get(section, "QuestionBlockPermissions")', get(section, "QuestionBlockPermissions"));
      let isViewable = this.questionService.checkIfViewable(get(section, "QuestionBlockPermissions"), this.state.userRole, this.state.isDemo);
      if (nextPage.data.blockName.includes('DeclarationsOwner')){
        if (!this.state.content.menuData[this.state.content.menuData.findIndex(x => x.name == nextPage.data.blockName)].visible){
          isViewable= false;
        }
      }
      // exception for block where values are in form not state
      // blockCondition = hasBlockConditionalClause ? !this.processorService.analyzeConditionalArray(condition.ConditionalClause, questions, formValue) : false;
      //console.log('isViewable: ', isViewable);
      const blockCondition = hasBlockConditionalClause ? !this.processorService.isNavBlockVisible(condition.ConditionalClause, formValue, questions) : false;

      //console.log(nextPage.data.blockName, (blockCondition ? 'conditionally skipped' : 'displayed'));
      if (!blockCondition && isViewable) {
        //console.log('showing: ', nextPage.data.blockName);
        break;
      }
      i++;
    }
    return nextPage.path;
  }

  submitIllustrationPayloadValues(formValue: any, blockName: any) {
    this.appState$.pipe(first()).subscribe((state: any) => {
      this.state = state;
      const payload = {};
      payload[blockName] = { ...formValue };
      this.store.dispatch({
        type: UPDATE_CONTENT,
        payload: window['JSON'].parse(window['JSON'].stringify(payload))
      });
      // sets form value variable used in transformPayload method
      this.processorService.eraseShownQuestions(formValue);

      const sectionQuestions = this.questions.find(item => item.Name === blockName);

      // prepare payload (uses formValue clone because the recursive method mutates the passed-in object)
      const updatedPayload = this.processorService.transformPayload(cloneDeep(formValue), sectionQuestions.Questions);

      const { appAuthId } = state;
     // state['content']['SECTIONAProposedInsured'] = updatedPayload;
      //this.processorService.submitSection({ appAuthId: appAuthId, section: blockName, payload: updatedPayload });
      //this.nextSection('express/proposed-insured');
    });


  }

  // allows disabling the "Next" button from child components
  disableButton(yesNo: boolean) {
    this.buttonDisabled.next(yesNo);
  }

  // observable status for disabling the "Next" button
  disableButtonStatus(): Observable<boolean> {
    return this.buttonDisabled.asObservable();
  }

  // allows disabling the "add" button from child components
  disableMultipleReponseButton(yesNo: boolean) {
    this.multipleResponseButtonDisabled.next(yesNo);
  }

  // observable status for disabling the "add" button
  disableMultipleReponseButtonStatus(): Observable<boolean> {
    return this.multipleResponseButtonDisabled.asObservable();
  }

  getHideMenu(): Observable<boolean> {
    return this.hideMenu.asObservable();
  }

  setHideMenu(newValue: boolean): void {
    this.hideMenu.next(newValue);
  }

  watchForExit(): Observable<boolean> {
    return this.submitOnExit.asObservable();
  }

  fireSubmit(newValue: boolean): void {
    this.submitOnExit.next(newValue);
  }

  isReadyToSubmit(): Observable<boolean> {
    return this.submitReady.asObservable();
  }

  readyToSubmit(newValue: boolean): void {
    this.submitReady.next(newValue);
  }

  setBlockValidity(blockName: string, valid: boolean) {
    this.appState$.pipe(first()).subscribe((state: any) => {
      const blockIndex = state.content.menuData.findIndex(x => x.name === blockName);
      state.content.menuData[blockIndex].valid = valid;
      });
  }
}
